import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useLocation, useOutlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { getPublicProfile, getEventDetails, getGalleryAlbums, clearAllModuleCaches } from '../../firebase';
import { loyaltyApi } from '../../services/loyaltyApi';
import { clearFollowingCache } from '../../arena/state/ArenaFriendsService';
import { BottomNav } from '../Navigation/BottomNav';
import { PwaPrompts } from '../Common/PwaPrompts';
import { SharedAppMenu } from '../Navigation/SharedAppMenu';
import { NotificationDrawer } from '../Navigation/NotificationDrawer';
import { ShoppingCart, ArrowLeft, ChevronRight, ChevronLeft, Bell } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useLoyaltySummary } from '../../hooks/queries/useProfile';
import { useNotifications } from '../../hooks/useNotifications';
import toast from 'react-hot-toast';
import { PullToRefreshIndicator } from '../UI/PullToRefreshIndicator';
import { LiveScorecardBanner } from '../Scorecard/LiveScorecardBanner';
import './GlobalLayout.scss';

export const GlobalLayout: React.FC = () => {
  const { user, profile, logOut } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const outlet = useOutlet();
  const navigate = useNavigate();
  const path = location.pathname;
  const [designVersion, setDesignVersion] = useState<'v1' | 'v2'>(() => {
    const saved = localStorage.getItem('acs-design-version');
    if (saved === 'v2' || saved === 'v1') return saved;
    return (path.includes('/members/hub-v2') || path.includes('/feed-v2')) ? 'v2' : 'v1';
  });

  useEffect(() => {
    if (path.includes('/members/hub-v2') || path.includes('/feed-v2')) {
      localStorage.setItem('acs-design-version', 'v2');
      setDesignVersion('v2');
    } else if (path === '/members' || path === '/feed') {
      localStorage.setItem('acs-design-version', 'v1');
      setDesignVersion('v1');
    }
  }, [path]);

  const isHubV2 = designVersion === 'v2';
  const hubPath = isHubV2 ? '/members/hub-v2' : '/members';
  const isArenaHome = path === '/arena' || path === '/arena/';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [resolvedNames, setResolvedNames] = useState<Record<string, string>>({});
  const { data: loyaltySummary } = useLoyaltySummary(user?.uid);
  const { unreadCount, notifications } = useNotifications();

  // Celebrate newly unlocked achievements with a celebratory medal toast
  const seenAchievementNotifIdsRef = useRef<Set<string>>(new Set());
  const isInitialNotifsLoadRef = useRef(true);

  useEffect(() => {
    if (!notifications || notifications.length === 0) return;

    // On initial load, record all existing notification IDs so we don't toast past achievements
    if (isInitialNotifsLoadRef.current) {
      notifications.forEach((n) => seenAchievementNotifIdsRef.current.add(n.id));
      isInitialNotifsLoadRef.current = false;
      return;
    }

    // Identify brand-new unread achievement notifications
    const newAchievements = notifications.filter(
      (n) =>
        !n.isRead &&
        (n.type === 'achievement_unlocked' || n.type?.includes('achievement')) &&
        !seenAchievementNotifIdsRef.current.has(n.id)
    );

    newAchievements.forEach((item) => {
      seenAchievementNotifIdsRef.current.add(item.id);

      // Audio celebration chime if available
      try {
        const audio = new Audio('/Images/notify.mp3');
        audio.volume = 0.7;
        audio.play().catch(() => {});
      } catch (_) {}

      // Fire celebratory medal toast
      toast.custom(
        (t) => (
          <div
            onClick={() => {
              toast.dismiss(t.id);
              navigate(item.targetUrl || '/members/achievements');
            }}
            className={`cursor-pointer flex items-center gap-3.5 px-4 py-3 bg-slate-900 border-2 border-amber-500/80 rounded-2xl shadow-2xl text-white transform transition-all duration-300 max-w-sm sm:max-w-md ${
              t.visible ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 -translate-y-2'
            }`}
            style={{
              background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.98), rgba(15, 23, 42, 0.98))',
              boxShadow: '0 10px 25px -5px rgba(245, 158, 11, 0.3), 0 8px 10px -6px rgba(245, 158, 11, 0.2)'
            }}
          >
            <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-xl shadow-lg border border-amber-300/40">
              🎖️
            </div>
            <div className="flex-1 min-w-0 pr-2">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-black uppercase tracking-widest text-amber-400">Medal Unlocked</span>
                <span className="text-[10px] text-white/50">• Just now</span>
              </div>
              <h4 className="text-sm font-black text-white truncate m-0 mt-0.5">{item.title}</h4>
              <p className="text-xs text-white/70 line-clamp-1 m-0 mt-0.5">{item.snippet}</p>
            </div>
          </div>
        ),
        {
          id: `ach-toast-${item.id}`,
          duration: 6500,
          position: 'top-center'
        }
      );
    });
  }, [notifications, navigate]);
  const isScrolledRef = useRef(false);
  const queryClient = useQueryClient();

  // Ultra-lean, memory-safe global refresh handler
  const handleGlobalRefresh = useCallback(async () => {
    try {
      clearAllModuleCaches();
      loyaltyApi.invalidateLoyaltySummaryCache();
      clearFollowingCache();
      await queryClient.invalidateQueries();
    } catch (err) {
      console.warn('[GlobalLayout] Error invalidating queries during pull-to-refresh:', err);
    }

    // Notify all active page views to refresh their local states
    window.dispatchEvent(new CustomEvent('app:pull-to-refresh'));
    window.dispatchEvent(new CustomEvent('app:refresh'));

    // Check service worker for pending app updates
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) {
          reg.update().catch(() => {});
        }
      } catch {
        // Silently ignore SW update errors
      }
    }
  }, [queryClient]);
  
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('theme-dark', 'theme-light');
    if (!path.startsWith('/admin') && !isHubV2) {
      root.classList.add(`theme-${theme}`);
    } else {
      root.classList.add('theme-light'); // Admin and V2 pages always use light styling
    }
  }, [theme, path, isHubV2]);  // Reset scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [path]);

  const getTierClass = (tierName: string) => {
    const t = tierName?.toLowerCase() || '';
    if (t.includes('platinum')) return 'tier-platinum';
    if (t.includes('gold')) return 'tier-gold';
    if (t.includes('silver')) return 'tier-silver';
    return 'tier-bronze';
  };

  const tier = loyaltySummary?.tier || 'Member';
  const showLoyaltyBorder = loyaltySummary?.isPaid && tier && !tier.toLowerCase().includes('non-paid') && !tier.toLowerCase().includes('non paid');
  const avatarTierClass = showLoyaltyBorder ? getTierClass(tier) : '';

  // Detect active scrolling globally to dynamically suspend expensive backdrop filters
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    const handleScrollState = () => {
      setIsScrolling(true);
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
      }, 150); // 150ms debounce window
    };

    window.addEventListener('scroll', handleScrollState, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScrollState);
      clearTimeout(scrollTimeout);
    };
  }, []);

  useEffect(() => {
    let frameId = 0;

    const handleScroll = () => {
      if (frameId) return;

      frameId = window.requestAnimationFrame(() => {
        frameId = 0;
        const nextIsScrolled = window.scrollY > 20;

        if (isScrolledRef.current !== nextIsScrolled) {
          isScrolledRef.current = nextIsScrolled;
          setIsScrolled(nextIsScrolled);
        }
      });
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const parts = path.split('/').filter(Boolean);
    parts.forEach(async (part, idx) => {
      // Already resolved or not an ID part
      if (resolvedNames[part]) return;

      // Resolve member names
      if (idx > 0 && parts[idx - 1] === 'members') {
        const cachedMember = queryClient.getQueryData<any[]>(['members', 'default'])?.find((m: any) => m.id === part);
        if (cachedMember) {
          const name = cachedMember.nickname || cachedMember.displayName || part;
          setResolvedNames(prev => ({ ...prev, [part]: name }));
          return;
        }
        try {
          const profileData = await getPublicProfile(part);
          if (profileData) {
            const name = profileData.nickname || profileData.displayName || part;
            setResolvedNames(prev => ({ ...prev, [part]: name }));
          }
        } catch (err) {
          console.error("Error resolving member name in breadcrumbs:", err);
        }
      }
      
      // Resolve event names
      if (idx > 0 && (parts[idx - 1] === 'events' || parts[idx - 1] === 'event')) {
        const cachedEvent = queryClient.getQueryData<any[]>(['events', 'upcoming'])?.find((e: any) => e.id === part);
        if (cachedEvent?.title) {
          setResolvedNames(prev => ({ ...prev, [part]: cachedEvent.title }));
          return;
        }
        try {
          const eventData = await getEventDetails(part);
          if (eventData) {
            setResolvedNames(prev => ({ ...prev, [part]: eventData.title }));
          }
        } catch (err) {
          console.error("Error resolving event name in breadcrumbs:", err);
        }
      }

      // Resolve gallery album names
      if (idx > 0 && parts[idx - 1] === 'gallery') {
        try {
          const albums = await getGalleryAlbums();
          const album = albums.find(a => a.id === part);
          if (album) {
            setResolvedNames(prev => ({ ...prev, [part]: album.title }));
          }
        } catch (err) {
          console.error("Error resolving album name in breadcrumbs:", err);
        }
      }
    });
  }, [path, resolvedNames, queryClient]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleLogout = async () => {
    await logOut();
    setIsMenuOpen(false);
    navigate('/');
    window.location.reload();
  };

  // Generate breadcrumbs from current path
  const breadcrumbs = useMemo(() => {
    if (path === '/') return [];
    
    const parts = path.split('/').filter(p => p);
    const rootCrumb = isArenaHome
      ? { label: isHubV2 ? 'Club Hub' : 'Hub', path: hubPath, active: false }
      : { label: 'Home', path: '/', active: false };
    const crumbs = [rootCrumb];
    
    let currentPath = '';
    parts.forEach((part, idx) => {
      currentPath += `/${part}`;
      const isActive = idx === parts.length - 1;
      
      let label = part;
      if (part.toLowerCase() === 'scorecard') {
        label = 'Choose Participants';
      } else if (part.toLowerCase() === 'hub-v2') {
        label = 'Club Hub';
      } else if (idx > 0 && (parts[idx - 1] === 'members' || parts[idx - 1] === 'events' || parts[idx - 1] === 'gallery')) {
        label = resolvedNames[part] || (parts[idx - 1] === 'members' ? 'Profile' : parts[idx - 1] === 'events' ? 'Event' : 'Album');
      } else if (part.toLowerCase() === 'messages') {
        label = 'Inbox';
      } else if (part.toLowerCase() === 'members') {
        label = path.startsWith('/admin') ? 'People' : 'Hub';
      } else if (part.toLowerCase() === 'gallery') {
        label = 'Gallery';
      } else if (part.toLowerCase() === 'new' && path.startsWith('/admin/matches')) {
        label = 'Record New Match';
      } else if (part.toLowerCase() === 'edit' && path.startsWith('/admin/matches')) {
        label = 'Edit Match';
      } else {
        // Pascal Case / Title Case conversion
        label = (part || '')
          .split('-')
          .map(word => {
              if (!word) return '';
              return word.charAt(0).toUpperCase() + word.slice(1);
          })
          .join(' ');
      }

      crumbs.push({
        label,
        path: currentPath,
        active: isActive
      });
    });
    
    return crumbs;
  }, [path, resolvedNames, isArenaHome, isHubV2, hubPath]);

  const isArenaGame =
    (path.startsWith('/arena/') && path !== '/arena') ||
    path.startsWith('/play/') ||
    path.startsWith('/game/') ||
    path.startsWith('/table/') ||
    path.startsWith('/watch/') ||
    path.startsWith('/room/');

  if (isArenaGame) {
    return (
      <div className="fixed inset-0 w-full h-full bg-[#070a10] overflow-hidden">
        {outlet}
      </div>
    );
  }

  const isScorecardRoute = path.includes('scorecard');
  const isAdminRoute = path.startsWith('/admin');
  const isAdminSubPage = path.startsWith('/admin/') && path !== '/admin';
  const themeClass = (isAdminRoute || isHubV2) ? 'theme-light' : `theme-${theme}`;

  return (
    <div className={`global-layout-shell ${themeClass} ${isScrolling ? 'is-scrolling' : ''}`}>
      <PwaPrompts />
      
      {/* Shared Header */}
      <header className={`global-header ${isAdminRoute ? 'admin-header' : ''} ${isAdminSubPage ? 'admin-sub-page-header' : ''} ${isScrolled ? 'is-scrolled' : ''} ${isHubV2 ? 'light-header-override' : ''} ${isScorecardRoute ? 'scorecard-header-override' : ''}`}>
        {/* Left: iOS Back Chevron & Page Title */}
        <div className="breadcrumbs-container">
          {!['/', '/feed', '/feed-v2', '/members', '/events', '/messages', '/profile', '/admin', '/gallery'].includes(path) && 
           breadcrumbs.length > 1 && (
            <button 
              onClick={() => isArenaHome ? navigate(hubPath) : navigate(-1)} 
              className="breadcrumb-back-btn mr-1.5 flex items-center justify-center cursor-pointer"
              title={isArenaHome ? "Back to Hub" : "Back"}
              aria-label={isArenaHome ? "Back to Hub" : "Back"}
              style={{ background: 'none', border: 'none', padding: '4px' }}
            >
              <ChevronLeft size={20} style={{ display: 'block' }} />
            </button>
          )}
          {breadcrumbs.length > 0 && (
            <span
              onClick={isArenaHome ? () => navigate(hubPath) : undefined}
              onKeyDown={isArenaHome ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(hubPath);
                }
              } : undefined}
              role={isArenaHome ? 'button' : undefined}
              tabIndex={isArenaHome ? 0 : undefined}
              title={isArenaHome ? 'Back to Hub' : undefined}
              aria-label={isArenaHome ? 'Back to Hub' : undefined}
              className={`breadcrumb-item ${isArenaHome ? 'cursor-pointer hover:opacity-80' : 'active'} font-black tracking-tight`}
              style={{
                fontSize: '1.2rem',
                ...(isArenaHome ? { pointerEvents: 'auto', cursor: 'pointer' } : {})
              }}
            >
              {breadcrumbs[breadcrumbs.length - 1].label}
            </span>
          )}
        </div>

        <div className="header-right-actions">
          {/* In-App Notification Bell */}
          {user && path !== '/login' && path !== '/signup' && (
            <button
              id="header-notification-btn"
              onClick={() => setIsNotificationOpen(true)}
              className="header-icon-action-btn notif-bell-btn relative"
              title="Notifications"
              aria-label="Open notifications"
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="header-notif-badge">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
          )}

          {/* Avatar Menu Toggle (Replaces Hamburger Menu) */}
          {path !== '/login' && path !== '/signup' && (
            <button 
              id="menu-toggle-btn"
              onClick={toggleMenu} 
              className="header-avatar-link bg-transparent border-none p-0 focus:outline-none cursor-pointer" 
              title="Menu"
              aria-label="Open menu"
            >
              <div className="avatar-wrapper">
                {user && profile?.photoUrl ? (
                  <img src={profile.photoUrl} className={`user-avatar ${avatarTierClass}`} alt="Avatar" />
                ) : user ? (
                  <div className={`user-avatar-fallback ${avatarTierClass}`}>
                    <i className="bi bi-person-fill"></i>
                  </div>
                ) : (
                  <div className="user-avatar-fallback">
                    <i className="bi bi-person"></i>
                  </div>
                )}
                {user && profile?.isAdmin && (
                  <div className="admin-badge-icon" title="Administrator">
                    <i className="bi bi-shield-fill-check"></i>
                  </div>
                )}
              </div>
            </button>
          )}
        </div>
      </header>

      <SharedAppMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
      
      <NotificationDrawer 
        isOpen={isNotificationOpen} 
        onClose={() => setIsNotificationOpen(false)} 
      />

      {/* Pull-to-refresh disabled for now */}
      <PullToRefreshIndicator disabled={true} onRefresh={handleGlobalRefresh} />

      {/* Real-time Live Scorecard Notification Banner for Involved Players */}
      <LiveScorecardBanner />

      {/* Page Content */}
      <main className="global-content-area">
        {outlet}
      </main>

      {user && !path.startsWith('/admin') && !path.startsWith('/arena') && path !== '/messages/chat' && !path.startsWith('/gallery/') && !path.endsWith('/rate') && !isScorecardRoute && <BottomNav />}
    </div>
  );
};
